/**
 * Markdown utilities for normalizing AI-generated content
 *
 * AI-generated content often lacks proper newlines between markdown elements.
 * This utility normalizes content to ensure proper rendering by ReactMarkdown.
 */

/**
 * Reconstruct cover letter formatting from blob text.
 * This handles the case where ALL newlines were lost and we need to
 * insert them based on pattern detection in continuous text.
 */
export function reconstructCoverLetterFromBlob(content: string): string {
  if (!content) return '';

  let result = content;

  // First, normalize any existing line endings
  result = result.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

  // Check if content is already well-formatted (has multiple newlines)
  const newlineCount = (result.match(/\n/g) || []).length;
  const contentLength = result.length;
  // If there are reasonable newlines already, just do light normalization
  if (newlineCount > 10 && newlineCount > contentLength / 200) {
    return normalizeMarkdown(content);
  }

  // === AGGRESSIVE BLOB RECONSTRUCTION ===

  // STEP 0: Convert pipe separators to newlines in contact info section
  // Pattern: phone | email | website (common in AI output)
  result = result.replace(
    /(\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4})\s*\|\s*([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})\s*\|\s*((?:www\.|https?:\/\/)[^\s\n]+)/gi,
    '$1\n$2\n$3'
  );

  // Also handle just phone | email or email | website with pipes
  result = result.replace(
    /(\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4})\s*\|\s*([a-zA-Z0-9._%+-]+@)/g,
    '$1\n$2'
  );
  result = result.replace(
    /(@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})\s*\|\s*((?:www\.|https?:\/\/)[^\s\n|]+)/gi,
    '$1\n$2'
  );

  // STEP 1: Handle bold company name directly adjacent to address
  // Pattern: **Company Name**1234 or **Company Name** 1234
  result = result.replace(
    /(\*\*[A-Z][A-Za-z\s]+(?:Inc\.?|LLC|Corp\.?|Digital|Services|Solutions|Technologies|Consulting|Group)?\*\*)\s*(\d{1,5}\s*[A-Za-z])/gi,
    '$1\n\n$2'
  );

  // Pattern: Company name (no bold) followed by street number with no space
  result = result.replace(
    /^([A-Z][A-Za-z\s]+(?:Inc\.?|LLC|Corp\.?|Digital|Services|Solutions|Technologies|Consulting|Group)?)\s*(\d{1,5}\s+[A-Za-z])/m,
    '**$1**\n\n$2'
  );

  // 2. Street address followed by City, State ZIP
  result = result.replace(
    /(\d{1,5}\s+[A-Za-z][A-Za-z\s]+(?:Street|St\.?|Avenue|Ave\.?|Boulevard|Blvd\.?|Road|Rd\.?|Drive|Dr\.?|Lane|Ln\.?|Way|Court|Ct\.?|Circle|Cir\.?))\s+([A-Za-z][A-Za-z\s]+)\s*,?\s*([A-Z]{2})\s+(\d{5}(?:-\d{4})?)/gi,
    '$1\n$2, $3 $4'
  );

  // 3. City/State/ZIP followed by phone
  result = result.replace(
    /(\d{5}(?:-\d{4})?)\s*\n?\s*(\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4})/g,
    '$1\n$2'
  );

  // 4. Phone followed by email (if not already handled by pipe conversion)
  result = result.replace(
    /(\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4})\s+([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g,
    '$1\n$2'
  );

  // 5. Email followed by website (if not already handled)
  result = result.replace(
    /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})\s+((?:www\.|https?:\/\/)[^\s\n]+)/gi,
    '$1\n$2'
  );

  // 6. Website followed by date
  result = result.replace(
    /((?:www\.|https?:\/\/)[^\s\n]+)\s*\n?\s*((?:January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2},?\s+\d{4})/gi,
    '$1\n\n$2'
  );

  // 7. Date followed by VIA (handle both with and without existing **)
  // Pattern: "October 15, 2023**VIA" or "October 15, 2023VIA" or "October 15, 2023 VIA"
  result = result.replace(
    /((?:January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2},?\s+\d{4})\s*\*?\*?\s*(VIA\s+ELECTRONIC\s+SUBMISSION)\s*\*?\*?\s*/gi,
    '$1\n\n**$2**\n\n'
  );

  // 8. VIA ELECTRONIC SUBMISSION followed by Department/Agency (without proper break)
  result = result.replace(
    /(\*\*VIA\s+ELECTRONIC\s+SUBMISSION\*\*)\s*(Department|[A-Z][a-z]+\s+Agency)/gi,
    '$1\n\n$2'
  );

  // Also handle case where VIA is not already bold
  result = result.replace(
    /([^\n*])(VIA\s+ELECTRONIC\s+SUBMISSION)([^\n*])/gi,
    '$1\n\n**$2**\n\n$3'
  );

  // 9. Agency/Department followed by Contracting Officer
  result = result.replace(
    /(Department\s+of\s+[A-Za-z]+|[A-Z][A-Za-z]+\s+Agency)\s*(Contracting\s+Officer)/gi,
    '$1\n$2'
  );

  // 10. Contracting Officer followed by RE: (handle existing ** markers)
  // Pattern: "Contracting Officer**RE:**" or "Contracting OfficerRE:"
  result = result.replace(
    /(Contracting\s+Officer)\s*\*?\*?\s*(RE:|Re:)\s*\*?\*?\s*/gi,
    '$1\n\n**RE:** '
  );

  // 11. RE: line - ensure it ends before Solicitation/NAICS/Dear
  result = result.replace(
    /(\*\*RE:\*\*\s*[^\n]+?)\s*(\*?\*?Solicitation\s+Number|\*?\*?NAICS\s+Code|Dear\s+)/gi,
    '$1\n\n$2'
  );

  // 12. Solicitation Number on its own line (handle existing **)
  result = result.replace(
    /\s*\*?\*?(Solicitation\s+Number:?\s*\[[^\]]*\]|Solicitation\s+Number:?\s*[A-Z0-9-]+)\*?\*?\s*/gi,
    '\n**$1**\n'
  );

  // 13. NAICS Code on its own line (handle existing **)
  result = result.replace(
    /\s*\*?\*?(NAICS\s+Code:?\s*\[[^\]]*\]|NAICS\s+Code:?\s*\d+)\*?\*?\s*/gi,
    '**$1**\n\n'
  );

  // 14. Dear salutation - needs blank line before and after
  result = result.replace(
    /\s*(Dear\s+(?:Contracting\s+Officer|Mr\.|Ms\.|Dr\.|[A-Z][a-z]+)[^:\n]*:)\s*/gi,
    '\n\n$1\n\n'
  );

  // 15. Closing signatures
  result = result.replace(
    /\s*(Respectfully\s+submitted|Respectfully|Sincerely|Best\s+regards|Regards),?\s*/gi,
    '\n\n$1,\n\n'
  );

  // 16. Name and title after signature
  result = result.replace(
    /(Respectfully|Sincerely|Regards),\s*\n*\s*([A-Z][a-z]+\s+[A-Z][a-z]+)\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*(?:\s+Officer|\s+Manager|\s+Director|\s+President|\s+CEO|\s+VP)?)/gi,
    '$1,\n\n$2\n$3'
  );

  // 17. Enclosures section
  result = result.replace(
    /\s*(Enclosures?:)/gi,
    '\n\n**$1**\n'
  );

  // 18. List items after Enclosures
  result = result.replace(
    /(Enclosures?:)\s*([-•]?\s*Technical|[-•]?\s*Management|[-•]?\s*Past|[-•]?\s*Pricing|[-•]?\s*Volume)/gi,
    '$1\n- $2'
  );
  result = result.replace(
    /(Volume)\s+([-•]?\s*(?:Technical|Management|Past|Pricing|Volume))/gi,
    '$1\n- $2'
  );

  // 19. Paragraph breaks for new topics
  result = result.replace(
    /\.\s+(We\s+are\s+|Our\s+team\s+|Our\s+company\s+|Our\s+organization\s+|The\s+company\s+|This\s+proposal\s+|Thank\s+you\s+|We\s+look\s+forward\s+|We\s+appreciate\s+|We\s+have\s+|We\s+believe\s+|We\s+understand\s+|In\s+conclusion\s+|Additionally\s*,\s+|Furthermore\s*,\s+|Moreover\s*,\s+)/gi,
    '.\n\n$1'
  );

  // 20. Clean up malformed bold markers (** with spaces or doubled)
  result = result.replace(/\*\*\s*\*\*/g, '');  // Remove empty bold
  result = result.replace(/\*\*\s+/g, '** ');   // Space after opening
  result = result.replace(/\s+\*\*/g, ' **');   // Space before closing
  result = result.replace(/\*\*\*\*/g, '');     // Remove quadruple asterisks

  // 21. Clean up multiple spaces
  result = result.replace(/[ \t]+/g, ' ');

  // 22. Clean up excessive newlines
  result = result.replace(/\n{4,}/g, '\n\n\n');

  // 23. Trim lines
  result = result.split('\n').map(line => line.trim()).join('\n');

  return result.trim();
}

/**
 * Normalize markdown content to ensure proper formatting for ReactMarkdown.
 * Fixes common issues with AI-generated content that lacks proper line breaks.
 *
 * @param content - Raw markdown content from AI
 * @returns Normalized markdown with proper line breaks
 */
export function normalizeMarkdown(content: string): string {
  if (!content) return '';

  let normalized = content;

  // Step 1: Normalize line endings
  normalized = normalized.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

  // Step 2: Fix table formatting FIRST (most complex)
  // Tables must have: blank line before, header row, separator row, data rows, blank line after

  // Pattern: Find table-like content (pipes with content between them)
  // Match inline tables that got concatenated: "text | Col1 | Col2 | |---|---| | data1 | data2 |"
  normalized = normalized.replace(
    /([a-zA-Z.,!?;:'")\]])(\s*)\|([^|]+)\|([^|]+)\|(\s*)\|([-:]+)\|([-:|\s]+)\|/g,
    (match, beforeText, space1, col1, col2, space2, sep1, sepRest) => {
      return `${beforeText}\n\n| ${col1.trim()} | ${col2.trim()} |\n|${sep1}|${sepRest}|`;
    }
  );

  // Fix tables where header runs into separator: "|Col1|Col2||---|---|"
  normalized = normalized.replace(
    /\|([^|\n]+)\|([^|\n]+)\|\|/g,
    '| $1 | $2 |\n|'
  );

  // Ensure table separator row is on its own line
  normalized = normalized.replace(
    /\|([^|\n-:]+)\|\s*\|([-:]+\|)/g,
    '| $1 |\n|$2'
  );

  // Ensure each table row with data is on its own line
  // Pattern: "|data1|data2||data3|data4|" -> proper rows
  normalized = normalized.replace(
    /\|([^|\n]+)\|([^|\n]+)\|\|([^-])/g,
    '| $1 | $2 |\n| $3'
  );

  // Add blank line before tables (where a pipe follows text)
  normalized = normalized.replace(
    /([a-zA-Z.,!?;:'")\]])(\s*)\n?(\|[^|]+\|)/g,
    '$1\n\n$3'
  );

  // Step 3: Fix headers - AGGRESSIVE handling
  // Any header marker (##) that follows text needs double newline before it
  normalized = normalized.replace(/([^\n#])(#{1,6})\s+/g, '$1\n\n$2 ');
  normalized = normalized.replace(/([^\n])\n(#{1,6}\s)/g, '$1\n\n$2');

  // Add blank line after header line (before content)
  normalized = normalized.replace(/(#{1,6}\s[^\n]+)\n([^\n#\s|])/g, '$1\n\n$2');

  // Step 4: Fix cover letter / business letter patterns
  // Company info with pipes should be on separate lines
  normalized = normalized.replace(
    /([A-Za-z\s]+\d+[^|]+)\s*\|\s*([^|@]+@[^|]+)\s*\|\s*(www\.[^\s\n]+)/g,
    '$1\n$2\n$3'
  );

  // Phone numbers followed by email/website - separate lines
  normalized = normalized.replace(
    /(\(\d{3}\)\s*\d{3}[-.]?\d{4})\s*\|\s*([a-zA-Z0-9._%+-]+@)/g,
    '$1\n$2'
  );

  // Date followed by submission method
  normalized = normalized.replace(
    /(\w+\s+\d{1,2},\s+\d{4})\s+(VIA\s+[A-Z\s]+)/g,
    '$1\n\n$2\n\n'
  );

  // VIA ELECTRONIC SUBMISSION should be on its own line
  normalized = normalized.replace(
    /([^\n])(VIA\s+ELECTRONIC\s+SUBMISSION)/gi,
    '$1\n\n$2\n\n'
  );

  // RE: line should have line break before
  normalized = normalized.replace(
    /([^\n])\s*(RE:)/g,
    '$1\n\n**$2**'
  );

  // Solicitation Number should be on its own line, indented
  normalized = normalized.replace(
    /([^\n])\s*(Solicitation\s+Number:)/gi,
    '$1\n\n$2'
  );

  // NAICS Code should be on its own line
  normalized = normalized.replace(
    /([^\n])\s*(NAICS\s+Code:)/gi,
    '$1\n$2'
  );

  // Dear X: salutation needs line breaks
  normalized = normalized.replace(
    /([^\n])\s*(Dear\s+[^:]+:)/g,
    '$1\n\n$2\n\n'
  );

  // Step 5: Fix list items - ensure proper spacing
  normalized = normalized.replace(/([^\n])\n([-*]\s)/g, '$1\n\n$2');
  normalized = normalized.replace(/([^\n])\n(\d+\.\s)/g, '$1\n\n$2');

  // Step 6: Ensure blockquotes have proper spacing
  normalized = normalized.replace(/([^\n])\n(>\s)/g, '$1\n\n$2');

  // Step 7: Fix bold/italic text that runs into headers
  normalized = normalized.replace(/(\*{1,2}[^*]+\*{1,2})(#{1,6}\s)/g, '$1\n\n$2');

  // Step 8: Fix colon followed immediately by header
  normalized = normalized.replace(/(:)\s*(#{1,6}\s)/g, '$1\n\n$2');

  // Step 9: Ensure horizontal rules have proper spacing
  normalized = normalized.replace(/([^\n])\n?(---+|___+|\*\*\*+)\n?([^\n])/g, '$1\n\n$2\n\n$3');

  // Step 10: Fix periods followed by capital letters (sentence breaks that should be paragraphs)
  // Only apply to long sentences that look like they should be paragraphs
  normalized = normalized.replace(/\.\s+([A-Z][a-z])/g, '.\n\n$1');

  // Step 11: Clean up excessive blank lines (more than 2 newlines become 2)
  normalized = normalized.replace(/\n{4,}/g, '\n\n\n');

  // Step 12: Ensure consistent spacing around tables
  // Find table blocks and ensure they're properly separated
  normalized = normalizeTableBlocks(normalized);

  return normalized.trim();
}

/**
 * Normalize table blocks to ensure proper markdown table structure
 */
function normalizeTableBlocks(content: string): string {
  // Find patterns that look like table data
  // Pattern: multiple pipe-separated values that should be table rows

  // Handle the specific broken table pattern from the screenshot:
  // "text: | Contract Type | When Appropriate | Risk Allocation | |--------------- ... Well-defined"

  let result = content;

  // Find table headers (| Something | Something |) followed by separator (|---|)
  const tablePattern = /\|\s*([^|\n]+)\s*\|\s*([^|\n]+)\s*\|(?:\s*([^|\n]+)\s*\|)?\s*\|[-:]+\|/g;

  result = result.replace(tablePattern, (match, col1, col2, col3) => {
    if (col3) {
      return `\n\n| ${col1.trim()} | ${col2.trim()} | ${col3.trim()} |\n|---|---|---|`;
    }
    return `\n\n| ${col1.trim()} | ${col2.trim()} |\n|---|---|`;
  });

  // Ensure table rows are on separate lines
  // Pattern: "|value1|value2||value3|value4|" (missing newlines between rows)
  result = result.replace(/\|\s*\|\s*([^|\n-])/g, '|\n| $1');

  // Fix broken separator rows
  result = result.replace(/\|([-:]+)\|([-:]+)\|(?:\s*\|?([-:]+)\|)?/g, (match, s1, s2, s3) => {
    if (s3) {
      return `|${s1}|${s2}|${s3}|`;
    }
    return `|${s1}|${s2}|`;
  });

  return result;
}

/**
 * Format content specifically for cover letters
 * Applies business letter formatting rules
 * Now uses aggressive blob reconstruction for content with missing newlines
 */
export function formatCoverLetter(content: string): string {
  if (!content) return '';

  // First, try aggressive blob reconstruction if needed
  let formatted = reconstructCoverLetterFromBlob(content);

  // Then apply additional cover letter specific formatting

  // Company name at start should be bold (if not already)
  formatted = formatted.replace(/^([A-Za-z][A-Za-z\s]+(?:Inc\.?|LLC|Corp\.?|Digital|Services|Solutions)?)\n/m, '**$1**\n');

  // Address formatting - ensure city, state ZIP on own line
  formatted = formatted.replace(
    /(\d+[^,\n]+),?\s*([A-Za-z\s]+),?\s*([A-Z]{2})\s*(\d{5}(?:-\d{4})?)/g,
    '$1\n$2, $3 $4'
  );

  // Respectfully/Sincerely closings
  formatted = formatted.replace(
    /([^\n])(Respectfully|Sincerely|Best regards|Regards),?/gi,
    '$1\n\n$2,'
  );

  // Enclosures section
  formatted = formatted.replace(
    /([^\n])(Enclosures?:)/gi,
    '$1\n\n**$2**'
  );

  return formatted;
}

/**
 * Reconstruct technical/executive summary content from blob text
 */
export function reconstructSectionFromBlob(content: string): string {
  if (!content) return '';

  let result = content;

  // Normalize line endings
  result = result.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

  // Check if already well-formatted
  const newlineCount = (result.match(/\n/g) || []).length;
  const contentLength = result.length;
  if (newlineCount > 10 && newlineCount > contentLength / 200) {
    return normalizeMarkdown(content);
  }

  // === AGGRESSIVE RECONSTRUCTION FOR TECHNICAL SECTIONS ===

  // 1. Headers: # Title, ## Section, ### Subsection
  // Pattern: text followed by "# " should have newline before
  result = result.replace(/([^#\n])(\s*)(#{1,6})\s+([A-Z])/g, '$1\n\n$3 $4');

  // 2. Numbered sections like "1. ", "1.1 ", "2. " etc.
  result = result.replace(/([.!?])\s+(\d+\.(?:\d+\.?)?\s+[A-Z])/g, '$1\n\n$2');

  // 3. Break before common section titles
  const sectionTitles = [
    'Executive Summary',
    'Value Proposition',
    'Understanding of Requirements',
    'Key Discriminators',
    'Relevant Experience',
    'Technical Solution',
    'Management Approach',
    'Technical Approach',
    'Methodology',
    'Quality Assurance',
    'Risk Management',
    'Schedule',
    'Milestones',
    'Past Performance',
    'Personnel',
    'Staffing',
    'Certifications',
    'Compliance',
    'Conclusion',
  ];

  for (const title of sectionTitles) {
    const regex = new RegExp(`([^\\n])\\s*((?:#{1,6}\\s+)?${title})`, 'gi');
    result = result.replace(regex, '$1\n\n$2');
  }

  // 4. Table reconstruction
  // Pattern: "| Col1 | Col2 | Col3 |" followed by "|---|---|---|"
  result = result.replace(
    /([^|\n])\s*(\|[^|\n]+\|[^|\n]+\|(?:[^|\n]+\|)?)\s*(\|[-:]+\|[-:]+\|(?:[-:]+\|)?)/g,
    '$1\n\n$2\n$3'
  );

  // 5. Table rows that run together: "|val1|val2||val3|val4|" -> separate rows
  result = result.replace(/\|\s*\|\s*([^|\n-])/g, '|\n| $1');

  // 6. Bullet points
  result = result.replace(/([.!?])\s+([-•]\s+[A-Z])/g, '$1\n\n$2');
  result = result.replace(/([.!?])\s+(\*\s+[A-Z])/g, '$1\n\n$2');

  // 7. Numbered lists
  result = result.replace(/([.!?])\s+(\d+\)\s+)/g, '$1\n\n$2');

  // 8. Bold section headers
  result = result.replace(/([.!?])\s+(\*\*[A-Z][^*]+\*\*:?)/g, '$1\n\n$2');

  // 9. Clean up
  result = result.replace(/[ \t]+/g, ' ');
  result = result.replace(/\n{4,}/g, '\n\n\n');

  return result.trim();
}

/**
 * Format content specifically for pricing/cost sections
 * Ensures tables are properly structured
 */
export function formatPricingSection(content: string): string {
  if (!content) return '';

  // First reconstruct from blob if needed
  let formatted = reconstructSectionFromBlob(content);

  // Then apply additional pricing-specific formatting
  formatted = normalizeMarkdown(formatted);

  // Ensure pricing tables have proper structure
  // Look for patterns like "Contract Type | When Appropriate | Risk"
  formatted = formatted.replace(
    /(?:^|\n)([^|\n]+)\s*\|\s*([^|\n]+)\s*\|\s*([^|\n]+)(?=\s*\|?\s*[-:])/gm,
    '\n\n| $1 | $2 | $3 |'
  );

  // Ensure separator follows header
  formatted = formatted.replace(
    /(\|[^|\n]+\|[^|\n]+\|[^|\n]*\|)\n([^|])/g,
    '$1\n|---|---|---|\n$2'
  );

  // Fix pricing-specific patterns
  // Dollar amounts should be aligned in tables
  formatted = formatted.replace(
    /\$\s*(\d)/g,
    '$$$1'
  );

  return formatted;
}

/**
 * Strip citation markers from content for clean export
 *
 * @param text - Text with citation markers like [PP-1] or [PERSONNEL-2]
 * @returns Clean text without citation markers
 */
export function stripCitations(text: string): string {
  return text.replace(/\s*\[[A-Z]+-\d+(?:,\s*[^\]]+)?]\s*/gi, ' ').replace(/\s+/g, ' ').trim();
}

/**
 * Detect if content looks like a cover letter
 */
export function isCoverLetterContent(content: string): boolean {
  const coverLetterIndicators = [
    /dear\s+(contracting|mr\.|ms\.|dr\.)/i,
    /respectfully\s*(submitted|,)/i,
    /sincerely,/i,
    /via\s+electronic\s+submission/i,
    /re:\s*(proposal|solicitation|contract)/i,
  ];

  return coverLetterIndicators.some(pattern => pattern.test(content));
}

/**
 * Detect if content looks like a pricing section
 */
export function isPricingContent(content: string): boolean {
  const pricingIndicators = [
    /contract\s+type/i,
    /pricing\s+(strategy|approach|method)/i,
    /labor\s+(category|rate)/i,
    /cost\s+(element|summary|breakdown)/i,
    /\$[\d,]+/,
    /firm.fixed.price|ffp|t&m|time.and.materials/i,
  ];

  return pricingIndicators.some(pattern => pattern.test(content));
}

/**
 * Smart normalization that detects content type and applies appropriate formatting
 * Now uses aggressive blob reconstruction for content with missing newlines
 */
export function smartNormalize(content: string): string {
  if (!content) return '';

  // Check if content is a blob (very few newlines relative to length)
  const newlineCount = (content.match(/\n/g) || []).length;
  const contentLength = content.length;
  const isBlob = newlineCount < 5 || (contentLength > 200 && newlineCount < contentLength / 200);

  if (isCoverLetterContent(content)) {
    return formatCoverLetter(content);
  }

  if (isPricingContent(content)) {
    return formatPricingSection(content);
  }

  // For other content types, use section reconstruction if it's a blob
  if (isBlob) {
    return reconstructSectionFromBlob(content);
  }

  return normalizeMarkdown(content);
}

/**
 * Normalize content for storage - ensures consistent formatting before saving to database
 * This should be called before storing content to ensure new proposals have proper formatting
 */
export function normalizeForStorage(content: string, sectionType?: string): string {
  if (!content) return '';

  // Check if content is structured JSON - if so, preserve it as-is
  // This is critical for sections that return JSON objects (like pricing with CLIN data)
  try {
    const parsed = JSON.parse(content);
    if (typeof parsed === 'object' && parsed !== null) {
      // Check for structural indicators that this is structured proposal content
      const hasStructuredContent =
        // Pricing indicators
        parsed.contractTypeRecommendation ||
        parsed.clinStructure ||
        parsed.laborCategories ||
        parsed.costBreakdown ||
        // Technical indicators
        parsed.methodology ||
        parsed.toolsAndTechnologies ||
        // Management indicators
        parsed.keyRoles ||
        parsed.communicationPlan ||
        // Personnel indicators
        parsed.keyPersonnel ||
        // Past performance indicators
        parsed.contracts ||
        // Cover letter indicators
        parsed.companyInfo ||
        // Compliance indicators
        parsed.farCompliance;

      if (hasStructuredContent) {
        // Return the JSON string as-is to preserve structure
        return content;
      }
    }
  } catch {
    // Not valid JSON, continue with markdown normalization
  }

  // Apply appropriate normalization based on section type
  switch (sectionType) {
    case 'coverLetter':
    case 'cover_letter':
      return formatCoverLetter(content);
    case 'pricingStrategy':
    case 'pricing':
      return formatPricingSection(content);
    case 'executiveSummary':
    case 'executive_summary':
    case 'technicalApproach':
    case 'technical':
    case 'managementApproach':
    case 'management':
    case 'pastPerformance':
    case 'past_performance':
    case 'personnel':
    case 'compliance':
      return reconstructSectionFromBlob(content);
    default:
      return smartNormalize(content);
  }
}
